import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import axios from '../../../utils/axios';

const StatusUpdateControls = ({ asset, isEditing, onUpdate }) => {
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    newStatus: asset?.status || 'active',
    reason: '',
    notes: '',
    scheduledDate: '',
    notifyOwner: true
  });

  const statusOptions = [
    { 
      value: 'active', 
      label: 'Active', 
      description: 'Asset is in use and operational',
      color: 'bg-success text-success-foreground',
      icon: 'CheckCircle'
    },
    { 
      value: 'spare', 
      label: 'Spare', 
      description: 'Asset is available for deployment',
      color: 'bg-warning text-warning-foreground',
      icon: 'Package'
    },
    { 
      value: 'repair', 
      label: 'Under Repair', 
      description: 'Asset requires maintenance or repair',
      color: 'bg-error text-error-foreground',
      icon: 'Wrench'
    },
    { 
      value: 'retired', 
      label: 'Retired', 
      description: 'Asset is no longer in service',
      color: 'bg-secondary text-secondary-foreground',
      icon: 'Archive'
    }
  ];

  const reasonOptions = [
    { value: 'deployment', label: 'New Deployment' },
    { value: 'maintenance', label: 'Scheduled Maintenance' },
    { value: 'repair', label: 'Repair Required' },
    { value: 'upgrade', label: 'Hardware Upgrade' },
    { value: 'replacement', label: 'Replacement' },
    { value: 'end-of-life', label: 'End of Life' },
    { value: 'policy', label: 'Policy Change' },
    { value: 'other', label: 'Other' }
  ];

  // Get status history from asset - try multiple possible sources
  const getStatusHistory = () => {
    // Check if asset has statusHistory
    if (asset?.statusHistory && Array.isArray(asset.statusHistory) && asset.statusHistory.length > 0) {
      return asset.statusHistory;
    }
    
    // Check if asset has histories with status changes
    if (asset?.histories && Array.isArray(asset.histories)) {
      const statusHistories = asset.histories.filter(h => h.action === 'status_update' || h.action === 'Status Updated');
      if (statusHistories.length > 0) {
        return statusHistories.map(h => ({
          id: h.id,
          status: h.new_status || asset.status,
          previousStatus: h.old_status || 'unknown',
          changedBy: h.user?.name || h.changed_by || 'System',
          changedDate: h.created_at || h.updated_at || new Date().toISOString(),
          reason: h.reason || 'status_change',
          notes: h.details || h.notes || '',
          isCurrent: h.is_current || false
        }));
      }
    }
    
    // If no history, create a basic history from current status
    if (asset?.status) {
      return [{
        id: 'current',
        status: asset.status,
        previousStatus: null,
        changedBy: asset.owner?.name || 'System',
        changedDate: asset.updated_at || asset.created_at || new Date().toISOString(),
        reason: 'initial',
        notes: 'Initial status',
        isCurrent: true
      }];
    }
    
    return [];
  };

  const statusHistory = getStatusHistory();

  const getCurrentStatusInfo = () => {
    return statusOptions?.find(option => option?.value === asset?.status) || statusOptions?.[0];
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdate?.newStatus || !statusUpdate?.reason) {
      toast.error('Please select a status and reason for the change');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the update data
      const updateData = {
        ...asset,
        status: statusUpdate.newStatus,
        // Add status history entry
        status_history: [
          ...(asset.status_history || []),
          {
            status: statusUpdate.newStatus,
            previousStatus: asset.status,
            changedBy: 'Current User',
            changedDate: new Date().toISOString(),
            reason: statusUpdate.reason,
            notes: statusUpdate.notes || '',
            scheduledDate: statusUpdate.scheduledDate || null,
            isCurrent: true
          }
        ],
        last_updated: new Date().toISOString()
      };

      // Call the update API
      const response = await axios.put(`/assets/${asset.id}`, updateData);
      console.log('Status update response:', response.data);
      
      if (response.data && response.data.success !== false) {
        const updatedAsset = response.data.data || response.data;
        
        if (typeof onUpdate === 'function') {
          onUpdate(updatedAsset);
        }
        
        // Reset form
        setStatusUpdate({
          newStatus: statusUpdate.newStatus,
          reason: '',
          notes: '',
          scheduledDate: '',
          notifyOwner: true
        });
        setShowStatusChange(false);

        toast.success('Status updated successfully');

        // Send notification if enabled
        if (statusUpdate.notifyOwner && asset.owner?.email) {
          try {
            await axios.post('/notifications/send', {
              to: asset.owner.email,
              subject: 'Asset Status Change Notification',
              message: `Asset ${asset.asset_id || asset.name} status has been changed to ${statusUpdate.newStatus}`
            });
            console.log(`Notification sent to ${asset.owner.email}`);
          } catch (notifyError) {
            console.error('Failed to send notification:', notifyError);
          }
        }
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Get status info for a specific status value
  const getStatusInfo = (statusValue) => {
    return statusOptions?.find(opt => opt?.value === statusValue) || {
      label: statusValue || 'Unknown',
      color: 'bg-muted',
      icon: 'Circle'
    };
  };

  // Get reason label
  const getReasonLabel = (reasonValue) => {
    return reasonOptions?.find(r => r?.value === reasonValue)?.label || reasonValue || 'Unknown';
  };

  const currentStatusInfo = getCurrentStatusInfo();

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Status Management</h3>
        {isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowStatusChange(!showStatusChange)}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Update Status
          </Button>
        )}
      </div>

      {/* Current Status Display */}
      <div className="bg-muted rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${currentStatusInfo?.color || 'bg-muted'}`}>
              <Icon name={currentStatusInfo?.icon || 'Circle'} size={20} color="white" />
            </div>
            <div>
              <h4 className="text-lg font-medium text-foreground">{currentStatusInfo?.label || asset?.status || 'Unknown'}</h4>
              <p className="text-sm text-muted-foreground">{currentStatusInfo?.description || 'Status description not available'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Last Updated</p>
            <p className="font-medium text-foreground">
              {asset?.last_updated ? formatDate(asset?.last_updated) : 
               asset?.updated_at ? formatDate(asset?.updated_at) : 
               'Unknown'}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Status Actions */}
      {!showStatusChange && isEditing && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {statusOptions?.map((status) => (
            <Button
              key={status?.value}
              variant={asset?.status === status?.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setStatusUpdate({ ...statusUpdate, newStatus: status?.value });
                setShowStatusChange(true);
              }}
              disabled={asset?.status === status?.value || isSubmitting}
              className="flex flex-col items-center p-3 h-auto"
            >
              <Icon name={status?.icon} size={16} className="mb-1" />
              <span className="text-xs">{status?.label}</span>
            </Button>
          ))}
        </div>
      )}

      {/* Status Change Form */}
      {showStatusChange && isEditing && (
        <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 mb-6">
          <h4 className="text-md font-medium text-foreground mb-4">Update Asset Status</h4>
          
          <div className="space-y-4">
            <Select
              label="New Status"
              options={statusOptions?.map(opt => ({ 
                value: opt?.value, 
                label: opt?.label,
                description: opt?.description 
              }))}
              value={statusUpdate?.newStatus}
              onChange={(value) => setStatusUpdate({ ...statusUpdate, newStatus: value })}
              required
            />

            <Select
              label="Reason for Change"
              options={reasonOptions}
              value={statusUpdate?.reason}
              onChange={(value) => setStatusUpdate({ ...statusUpdate, reason: value })}
              required
              placeholder="Select reason for status change"
            />

            <Input
              label="Scheduled Date (Optional)"
              type="datetime-local"
              value={statusUpdate?.scheduledDate}
              onChange={(e) => setStatusUpdate({ ...statusUpdate, scheduledDate: e?.target?.value })}
              description="When should this status change take effect?"
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional Notes
              </label>
              <textarea
                value={statusUpdate?.notes}
                onChange={(e) => setStatusUpdate({ ...statusUpdate, notes: e?.target?.value })}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Add any additional notes about this status change..."
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="notifyOwnerStatus"
                checked={statusUpdate?.notifyOwner}
                onChange={(e) => setStatusUpdate({ ...statusUpdate, notifyOwner: e?.target?.checked })}
                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-ring"
              />
              <label htmlFor="notifyOwnerStatus" className="text-sm text-foreground">
                Notify asset owner of status change
              </label>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="ghost" onClick={() => setShowStatusChange(false)}>
                Cancel
              </Button>
              <Button 
                variant="default" 
                onClick={handleStatusUpdate}
                disabled={!statusUpdate?.newStatus || !statusUpdate?.reason || isSubmitting}
                iconName={isSubmitting ? 'Loader2' : 'Save'}
                iconPosition="left"
              >
                {isSubmitting ? 'Updating...' : 'Update Status'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Status History */}
      {statusHistory && statusHistory.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-foreground mb-4">Status History</h4>
          <div className="space-y-3">
            {statusHistory.map((record, index) => {
              const statusInfo = getStatusInfo(record?.status);
              const previousStatusInfo = record?.previousStatus ? getStatusInfo(record?.previousStatus) : null;
              
              return (
                <div key={record?.id || index} className="flex items-start space-x-4 p-3 border border-border rounded-lg">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      record?.isCurrent ? statusInfo?.color || 'bg-primary' : 'bg-muted'
                    }`}>
                      <Icon 
                        name={statusInfo?.icon || 'Circle'} 
                        size={14} 
                        color={record?.isCurrent ? 'white' : 'currentColor'}
                        className={!record?.isCurrent ? 'text-muted-foreground' : ''}
                      />
                    </div>
                    {index < statusHistory?.length - 1 && (
                      <div className="w-0.5 h-8 bg-border mt-2"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2 flex-wrap">
                        <span className="font-medium text-foreground">{statusInfo?.label || record?.status}</span>
                        {record?.previousStatus && (
                          <>
                            <Icon name="ArrowRight" size={12} className="text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">from {previousStatusInfo?.label || record?.previousStatus}</span>
                          </>
                        )}
                        {record?.isCurrent && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success text-white">
                            Current
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">{formatDate(record?.changedDate)}</span>
                    </div>
                    
                    <div className="text-sm text-muted-foreground mb-1">
                      Changed by {record?.changedBy || 'System'} • Reason: {getReasonLabel(record?.reason)}
                    </div>
                    
                    {record?.notes && (
                      <p className="text-sm text-foreground bg-muted/50 rounded p-2 mt-2">{record?.notes}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Show message if no history */}
      {(!statusHistory || statusHistory.length === 0) && (
        <div className="text-center py-4 text-muted-foreground">
          <p>No status history available</p>
        </div>
      )}
    </div>
  );
};

export default StatusUpdateControls;